import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useAddLegalNoteMutation, useUpdateLegalReviewMutation } from "@/api/renewals";
import { useGetActiveDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getUserDisplayName } from "@/helpers/userDisplay";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { LegalNote, LegalReview, LegalReviewStatus, LegalStatus } from "@/types/renewals";
import type { DirectoryUser } from "@/types/user";

import { LEGAL_REVIEW_LABELS, LEGAL_STATUS_LABELS, SELECT_CLASS } from "../constants";
import { cn } from "@/lib/utils";

interface Props {
  relationId: string;
  review: LegalReview;
  notes: LegalNote[];
  canEdit: boolean;
  // Users referenced by the review and the notes, loaded once by the page.
  directory: Map<string, DirectoryUser>;
}

interface ReviewValues {
  review_status: LegalReviewStatus | "";
  representative_user_id: string;
  legal_status: LegalStatus | "";
}

const LABEL_CLASS = "text-[11px] font-semibold uppercase tracking-wide text-gray-500";

export const LegalReviewSection = ({ relationId, review, notes, canEdit, directory }: Props) => {
  const { formatDateTime } = useDateFormatter();
  const { data: activeUsers = [] } = useGetActiveDirectoryUsersQuery(undefined, { skip: !canEdit });
  const [updateLegalReview, { isLoading: isSaving }] = useUpdateLegalReviewMutation();
  const [addLegalNote, { isLoading: isAdding }] = useAddLegalNoteMutation();
  const reviewForm = useForm<ReviewValues>();
  const noteForm = useForm<{ body: string }>({ defaultValues: { body: "" } });
  const { reset, resetDefaultValues } = reviewForm;
  // keepDirtyValues requires a dirtyFields subscription, also used by the save button.
  const { dirtyFields, isSubmitting } = reviewForm.formState;

  // Another owner or an added note can trigger a refetch while this form has local edits.
  React.useEffect(() => {
    reset(
      {
        review_status: review.review_status ?? "",
        representative_user_id: review.representative_user_id ?? "",
        legal_status: review.legal_status ?? "",
      },
      { keepDirtyValues: true },
    );
  }, [reset, review.review_status, review.representative_user_id, review.legal_status]);

  const representativeOptions = React.useMemo(() => {
    const options = activeUsers.map(user => ({ id: user.id, label: getUserDisplayName(user) }));
    const current = review.representative_user_id;
    if (current && !options.some(option => option.id === current)) {
      const user = directory.get(current);
      options.push({ id: current, label: user ? getUserDisplayName(user) : "Former user" });
    }
    return options.sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" }));
  }, [activeUsers, directory, review.representative_user_id]);

  const saveReview = reviewForm.handleSubmit(async values => {
    try {
      await updateLegalReview({
        relationId,
        data: {
          review_status: values.review_status || null,
          representative_user_id: values.representative_user_id || null,
          legal_status: values.legal_status || null,
        },
      }).unwrap();
      // Advance the saved baseline without overwriting edits made while the request was pending.
      resetDefaultValues(values);
      toast.success("Legal review saved");
    } catch {
      toast.error("Legal review could not be saved");
    }
  });

  const saveNote = noteForm.handleSubmit(async ({ body }) => {
    if (!body.trim()) return;
    try {
      await addLegalNote({ relationId, body: body.trim() }).unwrap();
      noteForm.reset({ body: "" });
    } catch {
      toast.error("Note could not be added");
    }
  });

  const authorLabel = (userId: string) => {
    const user = directory.get(userId);
    return user?.email || getUserDisplayName(user);
  };

  return (
    <section className="rounded-lg border border-[#e4ddd5] bg-[#fffdfb] p-5" aria-label="Legal review status">
      <h2 className="text-sm font-bold uppercase tracking-wide text-[#111b35]">Legal review status</h2>
      <form className="mt-4" onSubmit={saveReview}>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Legal review</span>
            <select
              className={cn(SELECT_CLASS, "w-full")}
              disabled={!canEdit}
              {...reviewForm.register("review_status")}
            >
              <option value="">Not set</option>
              {(Object.keys(LEGAL_REVIEW_LABELS) as LegalReviewStatus[]).map(option => (
                <option key={option} value={option}>
                  {LEGAL_REVIEW_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Legal representative</span>
            <select
              className={cn(SELECT_CLASS, "w-full")}
              disabled={!canEdit}
              {...reviewForm.register("representative_user_id")}
            >
              <option value="">Not assigned</option>
              {representativeOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Legal status</span>
            <select className={cn(SELECT_CLASS, "w-full")} disabled={!canEdit} {...reviewForm.register("legal_status")}>
              <option value="">Not set</option>
              {(Object.keys(LEGAL_STATUS_LABELS) as LegalStatus[]).map(option => (
                <option key={option} value={option}>
                  {LEGAL_STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {review.updated_at && review.updated_by_user_id
              ? `Last updated ${formatDateTime(review.updated_at)}`
              : canEdit
                ? "Not reviewed yet"
                : "Only the Contract Family folder owner can update the legal review."}
          </p>
          {canEdit ? (
            <Button
              type="submit"
              size="sm"
              disabled={isSaving || isSubmitting || Object.keys(dirtyFields).length === 0}
            >
              Save legal review
            </Button>
          ) : null}
        </div>
      </form>

      <h3 className={cn(LABEL_CLASS, "mt-6")}>Legal next action / comment</h3>
      <ul className="mt-2 flex flex-col gap-2" aria-label="Legal notes">
        {notes.map(note => (
          <li key={note.id} className="rounded-md border border-[#e4ddd5] bg-white px-3 py-2">
            <p className="text-xs text-gray-500">
              {authorLabel(note.author_user_id)} · {formatDateTime(note.created_at)}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#111b35]">{note.body}</p>
          </li>
        ))}
        {notes.length === 0 ? <li className="text-sm text-gray-500">No notes yet.</li> : null}
      </ul>
      {canEdit ? (
        <form className="mt-3 flex gap-2" onSubmit={saveNote}>
          <Input
            className="flex-1 bg-white"
            placeholder="Add a note"
            aria-label="Add a note"
            maxLength={4000}
            {...noteForm.register("body")}
          />
          <Button type="submit" variant="outline" disabled={isAdding}>
            Add note
          </Button>
        </form>
      ) : null}
    </section>
  );
};
